'use client'

import { useState, useEffect } from 'react'
import {
  X,
  Inbox,
  Trash2,
  CheckCircle,
  AlertCircle,
  Info,
  UserPlus,
  RefreshCw,
} from 'lucide-react'
import { useDialog } from '@/components/providers/DialogProvider'
import {
  useNotificationStore,
  type Notification as NotificationItem,
} from '@/lib/notifications'
import { useRouter } from '@/i18n/navigation'
import { api } from '@/lib/api'

interface InboxModalProps {
  isOpen: boolean
  onClose: () => void
}

// Add imports for apiRequest and toast store
import { apiRequest } from '@/lib/api/utils/requests'
import { useToastNotificationStore } from '@/lib/toast-notifications'

export function InboxModal({ isOpen, onClose }: InboxModalProps) {
  const {
    notifications,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    dismissNotification,
    deleteNotification,
    lastFetchCount,
  } = useNotificationStore()

  const router = useRouter()
  const { alert, confirm } = useDialog()
  const addToast = useToastNotificationStore(state => state.addToast)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [isDeleting, setIsDeleting] = useState(false)
  const [actionLoading, setActionLoading] = useState<number | null>(null)

  // Filters & pagination
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [page, setPage] = useState<number>(1)
  const [limit, setLimit] = useState<number>(20)
  const offset = (page - 1) * limit

  const filteredNotifications = notifications
    .filter(n => {
      if (!dateFrom && !dateTo) return true
      const created = new Date(n.createdAt)
      const fromOk = dateFrom ? created >= new Date(dateFrom) : true
      const toOk = dateTo ? created <= new Date(dateTo) : true
      return fromOk && toOk
    })
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

  const canPrev = page > 1
  const canNext = lastFetchCount >= limit // server returned full page; may have more

  // Fetch ALL notifications (including dismissed ones) when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications({
        includeRead: true,
        includeDismissed: true,
        limit,
        offset,
      })
    }
  }, [isOpen, fetchNotifications, limit, offset])

  // Refetch when paging changes
  useEffect(() => {
    if (isOpen) {
      fetchNotifications({
        includeRead: true,
        includeDismissed: true,
        limit,
        offset,
      })
    }
  }, [isOpen, page, limit, offset, fetchNotifications])

  // Selection helpers
  const handleToggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    if (selectedIds.size === filteredNotifications.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredNotifications.map(n => n.id)))
    }
  }

  const handlePrev = () => {
    if (!canPrev) return
    setPage(p => Math.max(1, p - 1))
  }

  const handleNext = () => {
    if (!canNext) return
    setPage(p => p + 1)
  }

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return

    const confirmed = await confirm({
      title: 'Delete Notifications',
      description: `Are you sure you want to delete ${selectedIds.size} notification(s)? This action cannot be undone.`,
      confirmLabel: 'Delete',
    })
    if (!confirmed) return

    setIsDeleting(true)
    try {
      for (const id of selectedIds) {
        await deleteNotification(id)
      }
      setSelectedIds(new Set())
      // Refresh to show all notifications including dismissed ones
      await fetchNotifications({
        includeRead: true,
        includeDismissed: true,
        limit,
        offset,
      })
      await fetchUnreadCount()
    } catch (error) {
      console.error('Failed to dismiss notifications:', error)
      await alert({
        title: 'Delete Failed',
        description: 'Failed to dismiss some notifications. Please try again.',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleActionClick = async (
    notification: NotificationItem,
    action: {
      type: string
      label?: string
      url?: string
      onSuccess?: { navigate?: string }
      // Add apiCall metadata support
      apiCall?: {
        method: string
        endpoint: string
        payload?: any
        onSuccess?: { navigate?: string }
      }
    }
  ) => {
    if (actionLoading) return

    try {
      setActionLoading(notification.id)

      if (action.type === 'accept') {
        const sessionId = notification.sessionId
        if (sessionId) {
          // Prefer backend-provided apiCall metadata if present
          let response
          if (action.apiCall?.endpoint) {
            const endpoint = action.apiCall.endpoint.replace(/^\/api/, '')
            response = await apiRequest<any>(endpoint, {
              method: action.apiCall.method || 'POST',
              body: action.apiCall.payload
                ? JSON.stringify(action.apiCall.payload)
                : undefined,
            })
          } else {
            // Fallback to legacy hardcoded API
            response = await api.agent.acceptSessionTransfer(sessionId)
          }

          if (response?.success) {
            await markAsRead(notification.id)

            // Show success toast
            addToast({
              type: 'success',
              title: 'Transfer Accepted',
              message: `You are now assigned to session ${String(sessionId).slice(-8)}`,
              urgent: false,
            })

            const navigateTo =
              action.onSuccess?.navigate || action.apiCall?.onSuccess?.navigate
            if (navigateTo) {
              router.push(navigateTo)
              onClose()
            } else {
              const { buildAgentDashboardChatUrl } = await import('@/lib/utils')
              const fallbackUrl = buildAgentDashboardChatUrl(sessionId)
              router.push(fallbackUrl)
              onClose()
            }
          } else {
            const errMsg = response?.error || 'Transfer acceptance failed'

            // Show error toast with appropriate title
            const toastTitle = errMsg.includes('already accepted')
              ? 'Already Accepted'
              : 'Session Already Assigned'

            addToast({
              type: 'warning',
              title: toastTitle,
              message: errMsg,
              urgent: false,
            })

            // Refresh notifications to get updated state from server
            await fetchNotifications({
              includeRead: true,
              includeDismissed: true,
              limit,
              offset,
            })
          }
        } else {
          addToast({
            type: 'error',
            title: 'Missing Session',
            message: 'No session ID found for this notification.',
            urgent: false,
          })
        }
      } else if (action.type === 'view' && action.url) {
        if (action.url.startsWith('/')) {
          router.push(action.url)
          onClose()
        } else {
          window.location.href = action.url
        }
      }
    } catch (err: any) {
      const errMsg =
        err?.message || 'Failed to execute action. Please try again.'
      addToast({
        type: 'error',
        title: 'Action Error',
        message: errMsg,
        urgent: false,
      })
    } finally {
      setActionLoading(null)
    }
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'handover_requested':
      case 'session_transferred':
        return <UserPlus className="h-5 w-5 text-blue-500" />
      case 'session_completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'system_alert':
      case 'urgent_notification':
        return <AlertCircle className="h-5 w-5 text-red-500" />
      case 'agent_mention':
        return <Info className="h-5 w-5 text-purple-500" />
      default:
        return <Info className="h-5 w-5 text-blue-500" />
    }
  }

  const getNotificationBgColor = (priority: string, isRead: boolean) => {
    if (isRead) return 'bg-white'

    switch (priority) {
      case 'urgent':
        return 'bg-red-50 border-l-4 border-l-red-500'
      case 'high':
        return 'bg-orange-50 border-l-4 border-l-orange-500'
      case 'normal':
        return 'bg-blue-50'
      case 'low':
        return 'bg-gray-50'
      default:
        return 'bg-blue-50'
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`
    return date.toLocaleDateString()
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <Inbox className="h-6 w-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">
                Notification Inbox
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  fetchNotifications({
                    includeRead: true,
                    includeDismissed: true,
                    limit,
                    offset,
                  })
                }
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                title="Refresh"
              >
                <RefreshCw className="h-5 w-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                title="Close"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Filters & Pagination Toolbar */}
          <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-600">
                  From
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={e => {
                    setPage(1)
                    setDateFrom(e.target.value)
                  }}
                  className="rounded border-gray-300 text-sm px-2 py-1"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-600">
                  To
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={e => {
                    setPage(1)
                    setDateTo(e.target.value)
                  }}
                  className="rounded border-gray-300 text-sm px-2 py-1"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={limit}
                onChange={e => {
                  setPage(1)
                  setLimit(Number(e.target.value))
                }}
                className="rounded border-gray-300 text-sm px-2 py-1"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <button
                onClick={handlePrev}
                disabled={!canPrev}
                className="px-3 py-1 text-sm rounded border border-gray-300 text-gray-700 disabled:opacity-50"
              >
                Prev
              </button>
              <span className="text-sm text-gray-600">Page {page}</span>
              <button
                onClick={handleNext}
                disabled={!canNext}
                className="px-3 py-1 text-sm rounded border border-gray-300 text-gray-700 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>

          {/* Toolbar */}
          {notifications.length > 0 && (
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={
                    selectedIds.size === filteredNotifications.length &&
                    filteredNotifications.length > 0
                  }
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  title="Select all"
                />
                <span className="text-sm text-gray-600">
                  {selectedIds.size > 0
                    ? `${selectedIds.size} selected`
                    : `${filteredNotifications.length} notification${filteredNotifications.length !== 1 ? 's' : ''}`}
                </span>
              </div>

              {selectedIds.size > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  disabled={isDeleting}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="h-4 w-4" />
                  {isDeleting ? 'Deleting...' : `Delete (${selectedIds.size})`}
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {filteredNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6">
                <Inbox className="h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Your inbox is empty
                </h3>
                <p className="text-sm text-gray-500 text-center max-w-md">
                  All notifications you receive will appear here. You can manage
                  and delete them as needed.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredNotifications.map(notification => {
                  const actions =
                    (notification.metadata?.actions as any[]) || []
                  const isSelected = selectedIds.has(notification.id)

                  return (
                    <div
                      key={notification.id}
                      className={`p-6 hover:bg-gray-50 transition-colors ${
                        isSelected
                          ? 'bg-blue-50'
                          : getNotificationBgColor(
                              notification.priority,
                              notification.isRead
                            )
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        {/* Checkbox */}
                        <div className="flex-shrink-0 pt-1">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelect(notification.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            onClick={e => e.stopPropagation()}
                          />
                        </div>

                        {/* Icon */}
                        <div className="flex-shrink-0">
                          {getNotificationIcon(notification.type)}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-base font-semibold text-gray-900">
                                  {notification.title}
                                </p>
                                {!notification.isRead && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    New
                                  </span>
                                )}
                                {notification.isDismissed && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                    Dismissed
                                  </span>
                                )}
                                {notification.priority === 'urgent' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                    Urgent
                                  </span>
                                )}
                                {notification.priority === 'high' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                    High Priority
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mt-2">
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-400 mt-2">
                                {formatTimestamp(notification.createdAt)}
                              </p>

                              {/* Action Buttons */}
                              {actions.length > 0 && (
                                <div className="mt-4 flex items-center gap-2 flex-wrap">
                                  {actions.map((action, idx) => {
                                    if (action.type === 'accept') {
                                      return (
                                        <button
                                          key={idx}
                                          onClick={() =>
                                            handleActionClick(
                                              notification,
                                              action
                                            )
                                          }
                                          disabled={
                                            actionLoading === notification.id
                                          }
                                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          {actionLoading === notification.id
                                            ? 'Processing...'
                                            : action.label || 'Accept'}
                                        </button>
                                      )
                                    }

                                    if (action.type === 'view') {
                                      return (
                                        <button
                                          key={idx}
                                          onClick={() =>
                                            handleActionClick(
                                              notification,
                                              action
                                            )
                                          }
                                          className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 hover:bg-blue-50 rounded-lg"
                                        >
                                          {action.label || 'View'}
                                        </button>
                                      )
                                    }

                                    return null
                                  })}
                                </div>
                              )}
                            </div>

                            {/* Delete button */}
                            <button
                              onClick={async e => {
                                e.stopPropagation()
                                const confirmed = await confirm({
                                  title: 'Delete Notification',
                                  description:
                                    'Are you sure you want to delete this notification?',
                                  confirmLabel: 'Delete',
                                })
                                if (!confirmed) {
                                  return
                                }

                                await deleteNotification(notification.id)
                                // Refresh to show updated state
                                await fetchNotifications({
                                  includeRead: true,
                                  includeDismissed: true,
                                  limit,
                                  offset,
                                })
                              }}
                              className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title={'Delete notification'}
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
